import { Toast } from '@components/Toast';
import { Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { StyledMUISecondaryButton as Button } from 'src/components/Button';
import { MatchReportTable } from 'src/components/MatchReport/MatchReportTable';
import { UpdateMatchReport } from 'src/components/MatchReport/UpdateMatchReport';
import { StepContent } from 'src/components/StepElements';
import { HeaderText } from 'src/pages/CreateNewProductPage/styles';
import { useTenant } from 'src/providers/Tenant';
import { useUser } from 'src/providers/User';
import { GA } from 'src/services/gtag';
import { getConfigurationMatchReportV3 } from 'src/services/productInventory';

interface MatchReportPageRedirectState {
  successMessage?: {
    title: string;
    body: string;
  };
}

export function MatchReportPage() {
  const { accountId } = useTenant();
  const { username, role } = useUser();
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location?.search);
  const configurationPID = searchParams.get('configurationPID') ?? '';
  const productName = searchParams.get('productName') ?? '';
  const salesBOMJobRunID = searchParams.get('salesBOMJobRunID') ?? '';
  const cadJobRunID = searchParams.get('cadJobRunID') ?? '';
  const { t } = useTranslation(['createProduct', 'common']);

  const { successMessage } = (location.state as MatchReportPageRedirectState) ?? {};

  const { isLoading, isError, isSuccess, data } = useQuery(
    ['getConfigurationMatchReportV3', salesBOMJobRunID, configurationPID],
    () => getConfigurationMatchReportV3(accountId, configurationPID, productName, salesBOMJobRunID)
  );

  useEffect(() => {
    const event = {
      account: accountId,
      configurationPID,
      productName,
      user: username!,
      role: role!,
    };

    GA.CreateNewProductWizard.wizardCompleted(event);
  }, [accountId, configurationPID, productName, username, role]);

  if (isLoading) {
    return (
      <StepContent>
        <Stack direction="column" spacing={2} alignItems="start">
          <HeaderText variant="body1" data-testid="loading-header">
            {t('common:loading')}
          </HeaderText>
        </Stack>
      </StepContent>
    );
  }

  if (isError) {
    return (
      <StepContent>
        <Stack direction="column" spacing={2} alignItems="start">
          <HeaderText variant="body1" data-testid="error-header">
            {t('createProduct:matchReport.anErrorOccurred')}
          </HeaderText>
          <Typography variant="body2" data-testid="error-text">
            {t('createProduct:matchReport.tryAgain')}
          </Typography>
          <Button data-testid="error-back-button" onClick={() => history.goBack()}>
            {t('common:backButton')}
          </Button>
        </Stack>
      </StepContent>
    );
  }

  if (isSuccess && !data) {
    return (
      <StepContent>
        <Stack direction="column" spacing={2} alignItems="start">
          <HeaderText variant="body1" data-testid="no-data-header">
            {t('createProduct:matchReport.noData')}
          </HeaderText>
          <Typography variant="body2" data-testid="no-data-text">
            {t('createProduct:matchReport.tryAgain')}
          </Typography>
          <Button data-testid="no-data-button" onClick={() => history.goBack()}>
            {t('common:backButton')}
          </Button>
        </Stack>
      </StepContent>
    );
  }

  return (
    <>
      <StepContent>
        <Stack
          data-testid="match-report-container"
          direction={{ xs: 'column', md: 'row' }}
          spacing={7}
          useFlexGap
          sx={{
            '& > *': {
              flex: { xs: '1 1 100%', md: '1 1 50%' }, // Full width on mobile, 50% on desktop
              maxWidth: { xs: '100%', md: '50%' }, // Ensure max width is 50% on desktop
            },
          }}
        >
          <MatchReportTable productName={productName} data={data} />
          <UpdateMatchReport
            productName={productName}
            unsuccessfullyMatched={data.unsuccessfullyMatched}
            configurationPID={configurationPID}
            salesBOMJobRunID={salesBOMJobRunID}
            cadJobRunID={cadJobRunID}
          />
        </Stack>
      </StepContent>
      <Toast
        dependency={successMessage}
        severity="success"
        title={successMessage?.title!}
        message={successMessage?.body}
        autoHideDuration={null}
      />
    </>
  );
}
